import { useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { searchHotelLocations } from '../../../api/endpoints/hotel';
import type { HotelLocation, HotelSearchRequest } from '../../../api/types/hotel';
import type { CorporateEmployee } from '../../../api/types/corporateEmployee';
import { PersonnelAutocomplete } from '../../../components/PersonnelAutocomplete';
import { COUNTRIES } from '../../../config/countries';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import type { HotelFlowState } from '../flowConfig';

export function SearchStep() {
  const flow = useBookingFlowContext<HotelFlowState>();
  const [destination, setDestination] = useState<HotelLocation | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [checkIn, setCheckIn] = useState<Dayjs | null>(dayjs());
  const [checkOut, setCheckOut] = useState<Dayjs | null>(dayjs());
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [nationalityType, setNationalityType] = useState('TR');
  const [employee, setEmployee] = useState<CorporateEmployee | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(inputValue), 350);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const locationsQuery = useQuery({
    queryKey: ['hotelLocations', debouncedQuery],
    queryFn: () => searchHotelLocations(debouncedQuery),
    enabled: debouncedQuery.trim().length >= 3,
  });

  // `hotelLocationType`: 2 = specific hotel, 1 = city/region (confirmed — see
  // api/types/hotel.ts's HotelLocation comment). Regions listed before hotels.
  const options = useMemo(() => {
    const raw = locationsQuery.data?.hotelLocations ?? [];
    return [...raw].sort((a, b) => (a.hotelLocationType ?? 0) - (b.hotelLocationType ?? 0));
  }, [locationsQuery.data]);

  const handleSearch = () => {
    setValidationError(null);
    if (!destination) {
      setValidationError('Bir lokasyon seçmelisiniz.');
      return;
    }
    if (!checkIn || !checkOut || !checkIn.isValid() || !checkOut.isValid()) {
      setValidationError('Giriş ve çıkış tarihlerini seçmelisiniz.');
      return;
    }
    if (!checkOut.isAfter(checkIn)) {
      setValidationError('Çıkış tarihi giriş tarihinden sonra olmalıdır.');
      return;
    }
    if (adults < 1) {
      setValidationError('En az 1 yetişkin gereklidir.');
      return;
    }

    const searchRequest: HotelSearchRequest = {
      locationKey: destination.locationId ?? '',
      locationType: destination.hotelLocationType,
      locationName: destination.name,
      startDate: checkIn.format('YYYY-MM-DDT00:00:00'),
      endDate: checkOut.format('YYYY-MM-DDT00:00:00'),
      night: checkOut.diff(checkIn, 'day'),
      language: 'tr',
      currencyType: 'TRY',
      passengersInfo: [
        {
          roomId: 1,
          adt: adults,
          child: children,
          childAge: Array.from({ length: children }, () => 7),
        },
      ],
      employeeID: employee?.employeeID,
      nationalityType,
      paging: { pageNumber: 1, dataCount: 20, isPageChanged: false },
    };

    flow.runStep({ searchRequest });
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Otel Arama
        </Typography>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={options}
              getOptionLabel={(opt) => opt.name ?? ''}
              value={destination}
              onChange={(_, value) => setDestination(value)}
              inputValue={inputValue}
              onInputChange={(_, value) => setInputValue(value)}
              loading={locationsQuery.isFetching}
              isOptionEqualToValue={(opt, val) => opt.locationId === val.locationId}
              noOptionsText={
                debouncedQuery.trim().length < 3 ? 'Aramak için en az 3 karakter girin' : 'Sonuç bulunamadı'
              }
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                const isHotel = option.hotelLocationType === 2;
                return (
                  <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isHotel ? (
                      <HotelIcon fontSize="small" color="action" />
                    ) : (
                      <LocationCityIcon fontSize="small" color="action" />
                    )}
                    <Typography variant="body2">{option.name}</Typography>
                  </Box>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Nereye gidiyorsunuz?" size="small" />}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <DatePicker
              label="Giriş tarihi"
              value={checkIn}
              onChange={setCheckIn}
              minDate={dayjs()}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <DatePicker
              label="Çıkış tarihi"
              value={checkOut}
              onChange={setCheckOut}
              minDate={checkIn ?? dayjs()}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PersonnelAutocomplete value={employee} onChange={setEmployee} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              label="Uyruk"
              size="small"
              value={nationalityType}
              onChange={(e) => setNationalityType(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Yetişkin"
              type="number"
              size="small"
              fullWidth
              value={adults}
              onChange={(e) => setAdults(Math.max(1, Number(e.target.value) || 1))}
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Çocuk"
              type="number"
              size="small"
              fullWidth
              value={children}
              onChange={(e) => setChildren(Math.max(0, Number(e.target.value) || 0))}
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" disabled={flow.status === 'running'} onClick={handleSearch}>
            {flow.status === 'running' ? 'Aranıyor…' : 'Otel Ara'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
